/**
 * P7 A12 — authoritative order live status via SSE + poll fallback.
 * notify ≠ truth: reconstruct from backend on visibility return / reconnect.
 *
 * Wire contract:
 * - Poll / decision go through ApiClient with relative `/customer/tenants/...`
 *   paths (tenant normalizer → `/api/v1/customer/tenants/...`). Never pass
 *   absolute `/api/v1/...` into `api.request` (double-prefix).
 * - SSE uses absolute `/api/v1/customer/tenants/...` + `withCredentials`
 *   (EventSource cannot attach Bearer; cookie jar is the SSE identity).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';

export type A11DecisionChoice = 'ACCEPT_REMAINDER' | 'CANCEL_ALL';

export interface OrderLiveUnavailableItem {
  lineId: number;
  productId: number | null;
  name: string | null;
  productName?: string | null;
  variantName?: string | null;
  slug: string | null;
  path: string | null;
}

export interface OrderLivePendingUnavailable {
  scope: 'ITEM' | 'ORDER';
  lineIds: number[];
  markedAt: string;
  /** Present when live DTO resolves product names/links (G17). */
  items?: OrderLiveUnavailableItem[];
}

export interface OrderLiveDecision {
  id: number;
  choice: A11DecisionChoice;
  scope: 'ITEM' | 'ORDER';
  decidedAt: string;
}

/** Spec §10 — apology + new ETA + next step (PROMISED_ETA_UPDATED). */
export interface OrderLiveDelayNotice {
  apology: string;
  promisedPickupAt: string;
  nextStep: string;
  previousPromisedPickupAt: string | null;
}

/** Spec §10 — tenant DELAY/PAUSE impact on existing orders. */
export interface OrderLiveOpsNotice {
  mode: 'DELAY' | 'PAUSE';
  impact: string;
  opsEndsAt: string | null;
}

/** Spec §10 — no-show deadline + fate when READY_FOR_PICKUP. */
export interface OrderLiveNoShowNotice {
  deadlineAt: string;
  fate: string;
}

export interface OrderLiveStatusSnapshot {
  transactionId: number;
  fulfillmentId: number;
  fulfillmentMode: 'PICKUP' | 'SHIPPING' | string;
  status: string;
  version: number;
  pendingUnavailable: OrderLivePendingUnavailable | null;
  decision: OrderLiveDecision | null;
  delayNotice: OrderLiveDelayNotice | null;
  opsNotice: OrderLiveOpsNotice | null;
  noShowNotice: OrderLiveNoShowNotice | null;
  /** Sealed SHIPPING deadline (DeliveryPolicy) — optional while API in flux. */
  promisedDeliveryByAt?: string | null;
  autoDeliverEligibleAt?: string | null;
  lateComplaintFiledAt?: string | null;
  /** Derived from BE: any late complaint ever filed — permanent CTA hide. */
  lateComplaintEverFiled?: boolean;
  tenantHandoffKind?:
    | 'DISPATCHED_TO_CARRIER'
    | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
    | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
    | null;
  /** Sealed shipping method/policy blob from live DTO (SHIPPING). */
  shippingMethodSnapshot?: unknown | null;
  authoritative: true;
  lastEventId: number;
}

export interface UseOrderLiveStatusResult {
  snapshot: OrderLiveStatusSnapshot | null;
  isConnected: boolean;
  isPolling: boolean;
  error: string | null;
  reconstruct: () => Promise<void>;
  submitDecision: (choice: A11DecisionChoice) => Promise<void>;
  /**
   * Spec §8 A10 — POST .../fulfillments/:id/cancel (snapshot money on backend).
   * Pass `fulfillmentId` when cancelling from detail DTO before live snapshot arrives.
   */
  cancelFulfillment: (opts?: { fulfillmentId?: number }) => Promise<void>;
}

/** Relative path for ApiClient → `/api/{tenant}/v1/customer/tenants/...`. */
export function eventsApiPath(tenantCode: string, orderId: number): string {
  return `/customer/tenants/${encodeURIComponent(tenantCode)}/orders/${orderId}/events`;
}

/** Relative path for ApiClient POST customer-decision. */
export function decisionApiPath(tenantCode: string, fulfillmentId: number): string {
  return `/customer/tenants/${encodeURIComponent(tenantCode)}/fulfillments/${fulfillmentId}/customer-decision`;
}

/** Relative path for ApiClient POST A10 customer cancel. */
export function cancelFulfillmentApiPath(tenantCode: string, fulfillmentId: number): string {
  return `/customer/tenants/${encodeURIComponent(tenantCode)}/fulfillments/${fulfillmentId}/cancel`;
}

/** Statuses CancelCustomerFulfillmentUseCase accepts (A10). */
export const CUSTOMER_CANCEL_ALLOWED_STATUSES = [
  'PENDING_PAYMENT',
  'ACCEPTED',
  'PREPARING',
  'READY_FOR_PICKUP',
  'READY_TO_SHIP',
] as const;

export type CustomerCancelAllowedStatus = (typeof CUSTOMER_CANCEL_ALLOWED_STATUSES)[number];

export function isCustomerCancelAllowedStatus(status: string | null | undefined): boolean {
  if (status == null || status.length === 0) {
    return false;
  }
  return (CUSTOMER_CANCEL_ALLOWED_STATUSES as readonly string[]).includes(status);
}

/** Absolute SSE URL (apiV1Router mount; cookie credentials). */
export function eventsSseUrl(
  apiBaseUrl: string,
  tenantCode: string,
  orderId: number,
  query: URLSearchParams
): string {
  const base = apiBaseUrl.replace(/\/$/, '');
  const path = `/api/v1/customer/tenants/${encodeURIComponent(tenantCode)}/orders/${orderId}/events`;
  const qs = query.toString();
  return qs.length > 0 ? `${base}${path}?${qs}` : `${base}${path}`;
}

/** Maps BE GetCustomerOrderLiveStatusUseCase payload → FE snapshot (G5). */
export function mapOrderLiveStatusSnapshot(data: {
  transactionId: number;
  fulfillmentId: number;
  fulfillmentMode?: 'PICKUP' | 'SHIPPING' | string;
  status: string;
  version: number;
  events?: Array<{ id: number }>;
  pendingUnavailable: OrderLivePendingUnavailable | null;
  decision: OrderLiveDecision | null;
  delayNotice?: OrderLiveDelayNotice | null;
  opsNotice?: OrderLiveOpsNotice | null;
  noShowNotice?: OrderLiveNoShowNotice | null;
  promisedDeliveryByAt?: string | null;
  autoDeliverEligibleAt?: string | null;
  lateComplaintFiledAt?: string | null;
  lateComplaintEverFiled?: boolean;
  shippingMethodSnapshot?: unknown | null;
  tenantHandoffKind?:
    | 'DISPATCHED_TO_CARRIER'
    | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
    | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
    | null;
  shipment?: {
    promisedDeliveryByAt?: string | null;
    autoDeliverEligibleAt?: string | null;
    lateComplaintFiledAt?: string | null;
    lateComplaintEverFiled?: boolean;
    shippingMethodSnapshot?: unknown | null;
    tenantHandoffKind?:
      | 'DISPATCHED_TO_CARRIER'
      | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
      | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
      | null;
  } | null;
}): OrderLiveStatusSnapshot {
  const lastEvent = data.events != null ? data.events[data.events.length - 1] : undefined;
  const lastEventId = lastEvent != null ? lastEvent.id : 0;
  const shipment = data.shipment ?? null;
  const lateComplaintFiledAt =
    data.lateComplaintFiledAt ?? shipment?.lateComplaintFiledAt ?? null;
  const lateComplaintEverFiled =
    data.lateComplaintEverFiled === true ||
    shipment?.lateComplaintEverFiled === true ||
    (lateComplaintFiledAt != null && String(lateComplaintFiledAt).length > 0);
  return {
    transactionId: data.transactionId,
    fulfillmentId: data.fulfillmentId,
    fulfillmentMode: data.fulfillmentMode ?? 'PICKUP',
    status: data.status,
    version: data.version,
    pendingUnavailable: data.pendingUnavailable,
    decision: data.decision,
    delayNotice: data.delayNotice ?? null,
    opsNotice: data.opsNotice ?? null,
    noShowNotice: data.noShowNotice ?? null,
    promisedDeliveryByAt:
      data.promisedDeliveryByAt ?? shipment?.promisedDeliveryByAt ?? null,
    autoDeliverEligibleAt:
      data.autoDeliverEligibleAt ?? shipment?.autoDeliverEligibleAt ?? null,
    lateComplaintFiledAt,
    lateComplaintEverFiled,
    shippingMethodSnapshot:
      data.shippingMethodSnapshot ?? shipment?.shippingMethodSnapshot ?? null,
    tenantHandoffKind:
      data.tenantHandoffKind ?? shipment?.tenantHandoffKind ?? null,
    authoritative: true,
    lastEventId,
  };
}

/** @deprecated Use mapOrderLiveStatusSnapshot — kept as internal alias. */
const mapSnapshot = mapOrderLiveStatusSnapshot;

export function useOrderLiveStatus(orderId: number | null): UseOrderLiveStatusResult {
  const api = useApiClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const [snapshot, setSnapshot] = useState<OrderLiveStatusSnapshot | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastEventIdRef = useRef(0);
  const esRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pollOnce = useCallback(async (): Promise<void> => {
    if (orderId == null || orderId <= 0 || !tenantCode) {
      return;
    }
    const after = lastEventIdRef.current;
    const query: Record<string, string | number | undefined> = { mode: 'poll' };
    if (after > 0) {
      query.afterEventId = after;
    }
    // ApiClient unwraps `{ success, data }` → returns `data` only.
    const payload = await api.request<Parameters<typeof mapSnapshot>[0]>(
      eventsApiPath(tenantCode, orderId),
      { query }
    );
    if (payload == null) {
      throw new Error('Could not load order live status. Retry in a moment.');
    }
    const mapped = mapSnapshot(payload);
    if (mapped.lastEventId > 0) {
      lastEventIdRef.current = mapped.lastEventId;
    }
    setSnapshot(mapped);
    setError(null);
  }, [api, orderId, tenantCode]);

  const startPolling = useCallback((): void => {
    if (pollTimerRef.current != null) {
      return;
    }
    setIsPolling(true);
    setIsConnected(false);
    void pollOnce().catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Live status poll failed');
    });
    pollTimerRef.current = setInterval(() => {
      void pollOnce().catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Live status poll failed');
      });
    }, 4000);
  }, [pollOnce]);

  const stopPolling = useCallback((): void => {
    if (pollTimerRef.current != null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const connectSse = useCallback((): void => {
    if (orderId == null || orderId <= 0 || !tenantCode) {
      return;
    }
    esRef.current?.close();
    stopPolling();

    const qs = new URLSearchParams({ mode: 'sse' });
    if (lastEventIdRef.current > 0) {
      qs.set('afterEventId', String(lastEventIdRef.current));
    }
    const url = eventsSseUrl(apiBaseUrl ?? '', tenantCode, orderId, qs);
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.addEventListener('order_live_status', (event: MessageEvent<string>) => {
      try {
        const data = JSON.parse(event.data) as Parameters<typeof mapSnapshot>[0];
        const mapped = mapSnapshot(data);
        if (mapped.lastEventId > 0) {
          lastEventIdRef.current = mapped.lastEventId;
        }
        setSnapshot(mapped);
        setIsConnected(true);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Invalid live status payload');
      }
    });

    es.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setIsConnected(false);
      startPolling();
    };
  }, [apiBaseUrl, orderId, startPolling, stopPolling, tenantCode]);

  const reconstruct = useCallback(async (): Promise<void> => {
    // notify ≠ truth — always re-fetch authoritative backend state
    await pollOnce();
  }, [pollOnce]);

  const submitDecision = useCallback(
    async (choice: A11DecisionChoice): Promise<void> => {
      if (!snapshot || !tenantCode) {
        throw new Error('Order live status not ready');
      }
      await api.request(decisionApiPath(tenantCode, snapshot.fulfillmentId), {
        method: 'POST',
        body: { choice },
      });
      await reconstruct();
    },
    [api, reconstruct, snapshot, tenantCode]
  );

  const cancelFulfillment = useCallback(
    async (opts?: { fulfillmentId?: number }): Promise<void> => {
      if (!tenantCode) {
        throw new Error('Order live status not ready');
      }
      const fulfillmentId = opts?.fulfillmentId ?? snapshot?.fulfillmentId;
      if (fulfillmentId == null || fulfillmentId <= 0) {
        throw new Error('Fulfillment id is required to cancel');
      }
      if (snapshot != null && !isCustomerCancelAllowedStatus(snapshot.status)) {
        throw new Error('Order cannot be cancelled in its current status');
      }
      await api.request(cancelFulfillmentApiPath(tenantCode, fulfillmentId), {
        method: 'POST',
      });
      await reconstruct();
    },
    [api, reconstruct, snapshot, tenantCode]
  );

  useEffect(() => {
    if (orderId == null || orderId <= 0) {
      return;
    }
    let cancelled = false;
    // Defer setState — react-hooks/set-state-in-effect forbids sync setState in effect body.
    queueMicrotask(() => {
      if (!cancelled) {
        connectSse();
      }
    });
    return () => {
      cancelled = true;
      esRef.current?.close();
      esRef.current = null;
      stopPolling();
    };
  }, [connectSse, orderId, stopPolling]);

  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        void reconstruct().catch(() => {
          // reconstruct failure surfaces via next poll/SSE
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reconstruct]);

  return {
    snapshot,
    isConnected,
    isPolling,
    error,
    reconstruct,
    submitDecision,
    cancelFulfillment,
  };
}
